import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import useConfirm from '../../hooks/useConfirm';
import { 
  LabelEntry,
  LabelType, 
  useCreateLabelEntry, 
  useCreateLabelType, 
  useDeleteLabelEntry, 
  useDeleteLabelType, 
  useLabelEntries, 
  useLabelTypes, 
  useUpdateLabelEntry, 
  useUpdateLabelType 
} from '../../hooks/useLabels';
import { useSnackBar } from '../../hooks/useSnackBar';

function LabelManagement() {
  const { showSnackBar } = useSnackBar();
  
  // Quick add label dialog
  const [isQuickAddDialogOpen, setIsQuickAddDialogOpen] = useState(false);
  
  // State for quick add label dialog
  const [quickAddLabelType, setQuickAddLabelType] = useState<string>('');
  const [quickAddLabelEntry, setQuickAddLabelEntry] = useState<string>('');
  
  // State for new label type creation
  const [newLabelTypeValue, setNewLabelTypeValue] = useState<string>('');
  const [isAddingNewLabelType, setIsAddingNewLabelType] = useState(false);
  const [isCreatingLabelType, setIsCreatingLabelType] = useState(false);
  
  // State for new label entry creation
  const [newLabelEntryValue, setNewLabelEntryValue] = useState<string>('');
  const [isAddingNewLabelEntry, setIsAddingNewLabelEntry] = useState(false);
  const [isCreatingLabelEntry, setIsCreatingLabelEntry] = useState(false);
  
  // State for editing label types
  const [editingLabelTypeId, setEditingLabelTypeId] = useState<number | null>(null);
  const [editingLabelTypeValue, setEditingLabelTypeValue] = useState<string>('');
  const [isUpdatingLabelType, setIsUpdatingLabelType] = useState(false);
  
  // State for editing label entries
  const [editingLabelEntryId, setEditingLabelEntryId] = useState<number | null>(null);
  const [editingLabelEntryValue, setEditingLabelEntryValue] = useState<string>('');
  const [isUpdatingLabelEntry, setIsUpdatingLabelEntry] = useState(false);
  
  // State for deletion confirmation messages
  const [labelTypeToDelete, setLabelTypeToDelete] = useState<LabelType | null>(null);
  const [labelEntryToDelete, setLabelEntryToDelete] = useState<{id: number, title: string} | null>(null);
  
  // Create confirm dialogs at the component level with dynamic messages that update when state changes
  const deleteLabelTypeConfirm = useConfirm(
    {
      message: labelTypeToDelete ? `Are you sure you want to delete the label type "${labelTypeToDelete.labelType}"? This will also delete all associated label entries.` : '',
      confirmButtonColor: 'error',
      confirmButtonText: 'Delete'
    },
    [labelTypeToDelete]
  );
  
  const deleteLabelEntryConfirm = useConfirm(
    {
      message: labelEntryToDelete ? `Are you sure you want to delete the label entry "${labelEntryToDelete.title}"?` : '',
      confirmButtonColor: 'error',
      confirmButtonText: 'Delete'
    },
    [labelEntryToDelete]
  );

  // Fetch data
  const { data: labelTypesData, isLoading: isLoadingLabelTypes } = useLabelTypes();
  const { data: labelEntriesData, isLoading: isLoadingLabelEntries } = useLabelEntries();
  
  // Helper function to find label type ID by name
  const findLabelTypeId = useCallback((labelTypeName: string): number | null => {
    const labelType = labelTypesData?.collection?.find(
      (type: LabelType) => type.labelType.toLowerCase() === labelTypeName.toLowerCase()
    );
    return labelType?.id || null;
  }, [labelTypesData]);

  let filteredLabelEntries: Array<{title: string, id: number, labelTypeId: number}> = [];
  
  // Computed values for disabled states
  const isCreateLabelTypeDisabled = isCreatingLabelType || !newLabelTypeValue.trim();
  const isUpdateLabelTypeDisabled = isUpdatingLabelType || !editingLabelTypeValue.trim();
  const isCreateLabelEntryDisabled = isCreatingLabelEntry || !newLabelEntryValue.trim();
  const isUpdateLabelEntryDisabled = isUpdatingLabelEntry || !editingLabelEntryValue.trim();
  
  // Use useMemo to compute filteredLabelEntries
  filteredLabelEntries = useMemo(() => {
    if (!quickAddLabelType || !labelEntriesData?.collection) return [];
    
    // Find the label type ID
    const labelTypeId = findLabelTypeId(quickAddLabelType);
    
    if (!labelTypeId) return [];
    
    // Filter entries by label type ID
    return labelEntriesData.collection
      .filter((entry: LabelEntry) => entry.labelTypeId === labelTypeId)
      .map((entry: LabelEntry) => ({
        title: entry.entry,
        id: entry.id,
        labelTypeId: entry.labelTypeId
      }));
  }, [quickAddLabelType, labelEntriesData, findLabelTypeId]);
  
  // Mutation hooks
  const createLabelType = useCreateLabelType();
  const updateLabelType = useUpdateLabelType();
  const deleteLabelType = useDeleteLabelType();
  const createLabelEntry = useCreateLabelEntry();
  const updateLabelEntry = useUpdateLabelEntry();
  const deleteLabelEntry = useDeleteLabelEntry();
  
  // Helper function to handle async operations with loading state and error handling
  const handleAsyncOperation = async (
    operation: () => Promise<void>,
    setLoadingState: (loading: boolean) => void,
    successMessage: string,
    errorPrefix: string,
    successEvent: string,
    errorEvent: string
  ) => {
    try {
      setLoadingState(true);
      await operation();
      showSnackBar(successMessage, 'success', { event: successEvent });
      return true;
    } catch (error) {
      showSnackBar(`${errorPrefix}: ${(error as Error).message}`, 'error', { event: errorEvent });
      return false;
    } finally {
      setLoadingState(false);
    }
  };

  // Handler for deleting a label type
  const handleDeleteLabelType = async (type: LabelType, e: MouseEvent) => {
    e.stopPropagation();
    setLabelTypeToDelete(type);
    const confirmed = await deleteLabelTypeConfirm.windowDotConfirm();
    
    if (confirmed) {
      await handleAsyncOperation(
        async () => {
          await deleteLabelType.mutateAsync(type.id);
          if (quickAddLabelType === type.labelType) {
            setQuickAddLabelType('');
            setQuickAddLabelEntry('');
          }
        },
        () => {}, // No loading state for deletion
        `Label type "${type.labelType}" deleted successfully`,
        'Error deleting label type',
        'label_type_deleted',
        'label_type_delete_error'
      );
    }
  };
  
  // Handler for deleting a label entry
  const handleDeleteLabelEntry = async (entry: {id: number, title: string}, e: MouseEvent) => {
    e.stopPropagation();
    setLabelEntryToDelete(entry);
    const confirmed = await deleteLabelEntryConfirm.windowDotConfirm();
    
    if (confirmed) {
      await handleAsyncOperation(
        async () => {
          await deleteLabelEntry.mutateAsync(entry.id);
          if (quickAddLabelEntry === entry.title) {
            setQuickAddLabelEntry('');
          }
        },
        () => {}, // No loading state for deletion
        `Label entry "${entry.title}" deleted successfully`,
        'Error deleting label entry',
        'label_entry_deleted',
        'label_entry_delete_error'
      );
    }
  };

  // Reset all form and editing states
  const resetForm = () => {
    setQuickAddLabelType('');
    setQuickAddLabelEntry('');
    setNewLabelTypeValue('');
    setIsAddingNewLabelType(false);
    setNewLabelEntryValue('');
    setIsAddingNewLabelEntry(false);
    setEditingLabelTypeId(null);
    setEditingLabelTypeValue('');
    setEditingLabelEntryId(null);
    setEditingLabelEntryValue('');
  };

  // Handler for starting label type edit
  const handleSetLabelTypeEditing = (e: MouseEvent, type: LabelType | null) => {
    e.stopPropagation();
    setEditingLabelTypeId(type?.id || null);
    setEditingLabelTypeValue(type?.labelType || '');
  };

  // Handler for starting label entry edit
  const handleSetLabelEntryEditing = (e: MouseEvent, entry: {id: number, title: string, labelTypeId: number} | null) => {
    e.stopPropagation();
    setEditingLabelEntryId(entry?.id || null);
    setEditingLabelEntryValue(entry?.title || '');
  };

  // Helper function to validate non-empty input
  const validateInput = (value: string, errorMessage: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      showSnackBar(errorMessage, 'error', { event: 'label_validation_error' });
      return false;
    }
    return true;
  };

  // Handler for updating label type
  const handleUpdateLabelType = async (type: LabelType, e: MouseEvent) => {
    e.stopPropagation();
    const trimmedValue = editingLabelTypeValue.trim();
    
    if (!validateInput(trimmedValue, 'Please enter a label type')) return;
    
    await handleAsyncOperation(
      async () => {
        await updateLabelType.mutateAsync({ 
          id: type.id,
          labelType: trimmedValue 
        });
        
        // Update selected label type if it was the one being edited
        if (quickAddLabelType === type.labelType) {
          setQuickAddLabelType(trimmedValue);
        }
        
        setEditingLabelTypeId(null);
        setEditingLabelTypeValue('');
      },
      setIsUpdatingLabelType,
      'Label type updated successfully',
      'Error updating label type',
      'label_type_updated',
      'label_type_save_error'
    );
  };

  // Handler for updating label entry
  const handleUpdateLabelEntry = async (entry: {id: number, labelTypeId: number, title: string}, e: MouseEvent) => {
    e.stopPropagation();
    const trimmedValue = editingLabelEntryValue.trim();
    
    if (!validateInput(trimmedValue, 'Please enter a label entry')) return;
    
    await handleAsyncOperation(
      async () => {
        await updateLabelEntry.mutateAsync({ 
          id: entry.id,
          labelTypeId: entry.labelTypeId,
          entry: trimmedValue 
        });
        
        // Update selected label entry if it was the one being edited
        if (quickAddLabelEntry === entry.title) {
          setQuickAddLabelEntry(trimmedValue);
        }
        
        setEditingLabelEntryId(null);
        setEditingLabelEntryValue('');
      },
      setIsUpdatingLabelEntry,
      'Label entry updated successfully',
      'Error updating label entry',
      'label_entry_updated',
      'label_entry_save_error'
    );
  };

  // Handler for creating new label type
  const handleCreateLabelType = async () => {
    const trimmedValue = newLabelTypeValue.trim();
    
    if (!validateInput(trimmedValue, 'Please enter a label type')) return;
    
    await handleAsyncOperation(
      async () => {
        await createLabelType.mutateAsync({ 
          labelType: trimmedValue 
        });
        setQuickAddLabelType(trimmedValue);
        setIsAddingNewLabelType(false);
        setNewLabelTypeValue('');
      },
      setIsCreatingLabelType,
      `Label type "${trimmedValue}" created successfully`,
      'Error creating label type',
      'label_type_created',
      'label_type_save_error'
    );
  };

  // Handler for creating new label entry
  const handleCreateLabelEntry = async () => {
    const trimmedValue = newLabelEntryValue.trim();
    
    if (!validateInput(trimmedValue, 'Please enter a label entry')) return;
    
    // Find the label type ID
    const labelTypeId = findLabelTypeId(quickAddLabelType);
    
    if (!labelTypeId) {
      showSnackBar('Label type not found', 'error', {
        event: 'label_entry_save_error',
        label_type: quickAddLabelType,
      });
      return;
    }
    
    await handleAsyncOperation(
      async () => {
        // Create the new label entry
        await createLabelEntry.mutateAsync({ 
          labelTypeId, 
          entry: trimmedValue 
        });
        
        setIsAddingNewLabelEntry(false);
        setNewLabelEntryValue('');
      },
      setIsCreatingLabelEntry,
      `Label entry "${trimmedValue}" created successfully`,
      'Error creating label entry',
      'label_entry_created',
      'label_entry_save_error'
    );
  };

  return (
    <>
      <Button
        id="manage-labels-button"
        variant="contained"
        onClick={() => setIsQuickAddDialogOpen(true)}
        size="small"
      >
        Manage Labels
      </Button>

      {/* Quick Add Label Dialog */}
      <Dialog
        open={isQuickAddDialogOpen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Label Management</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">
              Select a Label Type
            </Typography>
              
            <TableContainer component={Paper} sx={{ maxHeight: 250 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="10%">ID</TableCell>
                    <TableCell>Label Type</TableCell>
                    <TableCell width="20%" align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {isLoadingLabelTypes ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : labelTypesData?.collection?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No label types found
                      </TableCell>
                    </TableRow>
                  ) : (
                    labelTypesData?.collection?.map((type: LabelType) => (
                      <TableRow 
                        key={type.id} 
                        hover
                        selected={quickAddLabelType === type.labelType}
                        onClick={() => setQuickAddLabelType(type.labelType)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{type.id}</TableCell>
                        <TableCell>
                          {editingLabelTypeId === type.id ? (
                            <TextField
                              fullWidth
                              size="small"
                              value={editingLabelTypeValue}
                              onChange={(e) => setEditingLabelTypeValue(e.target.value)}
                              autoFocus
                              disabled={isUpdatingLabelType}
                            />
                          ) : (
                            type.labelType
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {editingLabelTypeId === type.id ? (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <IconButton 
                                size="small" 
                                color="success" 
                                onClick={(e) => handleUpdateLabelType(type, e)}
                                disabled={isUpdateLabelTypeDisabled}
                              >
                                {isUpdatingLabelType ? <CircularProgress size={20} /> : <CheckIcon />}
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={(e) => handleSetLabelTypeEditing(e, null)}
                                disabled={isUpdatingLabelType}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => handleSetLabelTypeEditing(e, type)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleDeleteLabelType(type, e)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  
                  {/* Add new label type row */}
                  <TableRow>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => setIsAddingNewLabelType(true)}
                        disabled={isAddingNewLabelType}
                      >
                        <AddIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell colSpan={isAddingNewLabelType ? 1 : 2}>
                      {isAddingNewLabelType ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={newLabelTypeValue}
                          onChange={(e) => setNewLabelTypeValue(e.target.value)}
                          placeholder="Enter new label type"
                          autoFocus
                          disabled={isCreatingLabelType}
                        />
                      ) : (
                        <Typography color="textSecondary" variant="body2">
                          Add new label type
                        </Typography>
                      )}
                    </TableCell>
                    {isAddingNewLabelType && (
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton 
                            size="small" 
                            color="success" 
                            onClick={handleCreateLabelType}
                            disabled={isCreateLabelTypeDisabled}
                          >
                            {isCreatingLabelType ? <CircularProgress size={20} /> : <CheckIcon />}
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => {
                              setIsAddingNewLabelType(false);
                              setNewLabelTypeValue('');
                            }}
                            disabled={isCreatingLabelType}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {quickAddLabelType && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Available Entries for {quickAddLabelType}
                </Typography>
                
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="10%">ID</TableCell>
                        <TableCell>Entry</TableCell>
                        <TableCell width="20%" align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isLoadingLabelEntries ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <CircularProgress size={24} />
                          </TableCell>
                        </TableRow>
                      ) : filteredLabelEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No entries found for this label type
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLabelEntries.map((entry) => (
                          <TableRow 
                            key={entry.id} 
                            hover
                            selected={quickAddLabelEntry === entry.title}
                            onClick={() => setQuickAddLabelEntry(entry.title)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{entry.id}</TableCell>
                            <TableCell>
                              {editingLabelEntryId === entry.id ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={editingLabelEntryValue}
                                  onChange={(e) => setEditingLabelEntryValue(e.target.value)}
                                  autoFocus
                                  disabled={isUpdatingLabelEntry}
                                />
                              ) : (
                                entry.title
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {editingLabelEntryId === entry.id ? (
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <IconButton 
                                    size="small" 
                                    color="success" 
                                    onClick={(e) => handleUpdateLabelEntry(entry, e)}
                                    disabled={isUpdateLabelEntryDisabled}
                                  >
                                    {isUpdatingLabelEntry ? <CircularProgress size={20} /> : <CheckIcon />}
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={(e) => handleSetLabelEntryEditing(e, null)}
                                    disabled={isUpdatingLabelEntry}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => handleSetLabelEntryEditing(e, entry)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => handleDeleteLabelEntry(entry, e)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      
                      {/* Add new label entry row */}
                      <TableRow>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => setIsAddingNewLabelEntry(true)}
                            disabled={isAddingNewLabelEntry}
                          >
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell colSpan={isAddingNewLabelEntry ? 1 : 2}>
                          {isAddingNewLabelEntry ? (
                            <TextField
                              fullWidth
                              size="small"
                              value={newLabelEntryValue}
                              onChange={(e) => setNewLabelEntryValue(e.target.value)}
                              placeholder="Enter new label entry"
                              autoFocus
                              disabled={isCreatingLabelEntry}
                            />
                          ) : (
                            <Typography color="textSecondary" variant="body2">
                              Add new label entry
                            </Typography>
                          )}
                        </TableCell>
                        {isAddingNewLabelEntry && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <IconButton 
                                size="small" 
                                color="success" 
                                onClick={handleCreateLabelEntry}
                                disabled={isCreateLabelEntryDisabled}
                              >
                                {isCreatingLabelEntry ? <CircularProgress size={20} /> : <CheckIcon />}
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => {
                                  setIsAddingNewLabelEntry(false);
                                  setNewLabelEntryValue('');
                                }}
                                disabled={isCreatingLabelEntry}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => {
            setIsQuickAddDialogOpen(false);
            resetForm();
          }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LabelManagement;
